import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, Phone, X, Brain, Calendar, Sun, Moon, Globe, ChevronDown, Calculator } from 'lucide-react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';

const Header = ({ onOpenAnalysis, onOpenConsultation }) => {
  const { clinicInfo, features } = useLandingPageData();
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation(language);
  const location = useLocation();
  const showAIAnalysis = onOpenAnalysis && features?.showAIAnalysis !== false;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const treatmentLinks = [
    { href: '/', label: language === 'de' ? 'Haartransplantation' : 'Hair Transplant' },
    { href: '/bart', label: language === 'de' ? 'Barthaartransplantation' : 'Beard Transplant' },
    { href: '/augenbrauen', label: language === 'de' ? 'Augenbrauentransplantation' : 'Eyebrow Transplant' },
  ];

  const navLinks = [
    { href: '#vorher-nachher', label: t('nav.results') },
    { href: '#expertise', label: t('nav.whyUs') },
    { href: '#ablauf', label: t('nav.process') },
    { href: '#preise', label: t('nav.prices') },
    { href: '/preisrechner', label: language === 'de' ? 'Kostenrechner' : 'Price Calculator', isRoute: true },
    { href: '/blog', label: 'Blog', isRoute: true },
    { href: '#team', label: t('nav.team') },
    { href: '#kontakt', label: t('nav.contact') },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const handleDropdownEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTreatmentsOpen(true);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => setTreatmentsOpen(false), 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? isDark 
            ? 'bg-[#1a1a1a]/95 backdrop-blur-xl shadow-2xl shadow-black/20'
            : 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - links back to homepage */}
          <Link
            to="/"
            className="flex items-center group"
          >
            <span 
              className={`text-lg tracking-wide transition-colors duration-300 ${
                isDark 
                  ? 'text-white group-hover:text-[#7BA7C2]' 
                  : 'text-gray-900 group-hover:text-[#5A8BA6]'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 100 }}
            >
              Dion Hair Clinic
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {/* Behandlungen Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`px-3 py-2 text-sm whitespace-nowrap transition-colors duration-300 relative group flex items-center gap-1 ${
                  isDark 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="treatments-dropdown-btn"
              >
                {language === 'de' ? 'Behandlungen' : 'Treatments'}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${treatmentsOpen ? 'rotate-180' : ''}`} />
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] group-hover:w-3/4 transition-all duration-300" />
              </button>
              {treatmentsOpen && (
                <div
                  className={`absolute top-full left-0 mt-1 w-64 rounded-xl shadow-xl border overflow-hidden transition-all duration-200 ${
                    isDark
                      ? 'bg-[#1a1a1a]/95 backdrop-blur-xl border-white/10'
                      : 'bg-white/95 backdrop-blur-xl border-gray-200'
                  }`}
                >
                  {treatmentLinks.map((link) => {
                    const isActive = location.pathname === link.href;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setTreatmentsOpen(false)}
                        className={`block px-5 py-3 text-sm transition-all duration-200 ${
                          isActive
                            ? isDark
                              ? 'bg-[#7BA7C2]/15 text-[#7BA7C2]'
                              : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
                            : isDark
                              ? 'text-gray-300 hover:text-white hover:bg-white/5'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                        data-testid={`treatment-link-${link.href.replace('/', '') || 'home'}`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.href.replace('/', '')}`}
                  className={`px-3 py-2 text-sm whitespace-nowrap transition-colors duration-300 relative group ${
                    isDark 
                      ? 'text-gray-300 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] group-hover:w-3/4 transition-all duration-300" />
                </Link>
              ) : (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`px-3 py-2 text-sm whitespace-nowrap transition-colors duration-300 relative group ${
                    isDark 
                      ? 'text-gray-300 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {link.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] group-hover:w-3/4 transition-all duration-300" />
                </button>
              )
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden xl:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-label={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
              data-testid="language-toggle-desktop"
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-label={isDark ? t('nav.switchToLight') : t('nav.switchToDark')}
              data-testid="theme-toggle-desktop"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            
            <a
              href={`tel:${clinicInfo.phone.replace(/\s/g, '')}`}
              className={`flex items-center gap-2 text-sm transition-colors duration-300 ${
                isDark 
                  ? 'text-gray-300 hover:text-[#7BA7C2]' 
                  : 'text-gray-600 hover:text-[#5A8BA6]'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">{clinicInfo.phone}</span>
            </a>
            <Button
              onClick={onOpenConsultation}
              className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white border-0 shadow-lg shadow-[#7BA7C2]/25 hover:shadow-[#7BA7C2]/40 transition-all duration-300"
              data-testid="header-consultation-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t('nav.bookConsultation')}
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 xl:hidden">
            {/* Language Toggle Mobile */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-label={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
              data-testid="language-toggle-mobile"
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* Theme Toggle Mobile */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              aria-label={isDark ? t('nav.switchToLight') : t('nav.switchToDark')}
              data-testid="theme-toggle-mobile"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={isDark ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-gray-100'}
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className={`w-full sm:max-w-md ${
                  isDark 
                    ? 'bg-[#1a1a1a] border-[#2a2a2a]' 
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex flex-col h-full pt-8">
                  <nav className="flex flex-col gap-2">
                    {/* Behandlungen Accordion */}
                    <div>
                      <button
                        onClick={() => setMobileTreatmentsOpen(!mobileTreatmentsOpen)}
                        className={`w-full px-4 py-4 text-lg rounded-lg transition-all duration-300 text-left flex items-center justify-between ${
                          isDark 
                            ? 'text-gray-300 hover:text-white hover:bg-white/5' 
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        data-testid="mobile-treatments-toggle"
                      >
                        {language === 'de' ? 'Behandlungen' : 'Treatments'}
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileTreatmentsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileTreatmentsOpen && (
                        <div className="ml-4 flex flex-col gap-1">
                          {treatmentLinks.map((link) => {
                            const isActive = location.pathname === link.href;
                            return (
                              <Link
                                key={link.href}
                                to={link.href}
                                onClick={() => { setIsOpen(false); setMobileTreatmentsOpen(false); }}
                                className={`px-4 py-3 text-base rounded-lg transition-all duration-300 ${
                                  isActive
                                    ? isDark
                                      ? 'text-[#7BA7C2] bg-[#7BA7C2]/10'
                                      : 'text-[#5A8BA6] bg-[#5A8BA6]/10'
                                    : isDark
                                      ? 'text-gray-400 hover:text-white hover:bg-white/5'
                                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                                data-testid={`mobile-treatment-link-${link.href.replace('/', '') || 'home'}`}
                              >
                                {link.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {navLinks.map((link) => (
                      link.isRoute ? (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setIsOpen(false)}
                          data-testid={`mobile-nav-link-${link.href.replace('/', '')}`}
                          className={`px-4 py-4 text-lg rounded-lg transition-all duration-300 text-left ${
                            isDark 
                              ? 'text-gray-300 hover:text-white hover:bg-white/5' 
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <button
                          key={link.href}
                          onClick={() => scrollToSection(link.href)}
                          className={`px-4 py-4 text-lg rounded-lg transition-all duration-300 text-left ${
                            isDark 
                              ? 'text-gray-300 hover:text-white hover:bg-white/5' 
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {link.label}
                        </button>
                      )
                    ))}
                  </nav>
                  <div className="mt-auto pb-8 space-y-4">
                    <a
                      href={`tel:${clinicInfo.phone.replace(/\s/g, '')}`}
                      className={`flex items-center justify-center gap-2 py-3 rounded-lg transition-colors duration-300 ${
                        isDark 
                          ? 'text-[#7BA7C2] border border-[#7BA7C2]/30 hover:bg-[#7BA7C2]/10' 
                          : 'text-[#5A8BA6] border border-[#5A8BA6]/40 hover:bg-[#5A8BA6]/10'
                      }`}
                    >
                      <Phone className="w-5 h-5" />
                      {clinicInfo.phone}
                    </a>
                    <Button
                      onClick={() => {
                        setIsOpen(false);
                        onOpenConsultation();
                      }}
                      className="w-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white py-6 text-lg"
                      data-testid="mobile-consultation-btn"
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      {t('nav.bookConsultation')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;